/**
 * LS Klaviyo cart sync
 *
 * Sends ONE "Added to Cart" event per add-to-cart with the full, updated cart,
 * and keeps the profile property "CarrelloAttuale" in sync on every cart change
 * (add, change, update, clear). Used by the "Carrello abbandonato" flow in Klaviyo.
 *
 * How it works: a PerformanceObserver is notified when any cart AJAX request
 * (/cart/add|change|update|clear, via fetch, XHR or jQuery) has finished; the script then
 * re-reads cart.js (so the cart is always up to date) and only then sends the data to
 * Klaviyo. The added variant comes from the theme's "variant:added" event, else from
 * comparing the cart before and after, else from the first cart line (Shopify puts the
 * newest line first).
 * We do not wrap window.fetch: Shopify's own shop_events_listener also wraps it and,
 * depending on load order, could bypass our wrapper.
 *
 * Amounts are in the cart's currency (EUR, or USD for the US market): "Currency" says which.
 *
 * Spec: pedidos/2026-10-klaviyo-added-to-cart (technical note from Silvia).
 */
(() => {
  // Guard against double initialisation (the old code was hooked several times).
  if (window.__lsKlaviyoCart) return;
  window.__lsKlaviyoCart = true;

  // Locale root: '/' (IT), '/en/', '/en-us/', '/fr/', '/de/'.
  // Reading cart.js under the locale root returns translated product titles.
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const rootNoSlash = root.replace(/\/$/, '');
  const base = window.location.origin + rootNoSlash;
  const CART_ENDPOINT = /\/cart\/(add|change|update|clear)(?:\.js)?$/;
  let lastKey = '';

  const money = (cents) => Math.round(cents) / 100;

  // cart.js returns "/products/handle?variant=…"; add the locale prefix only if missing.
  const localizedUrl = (path) => {
    const clean = path.split('?')[0];
    if (!rootNoSlash || clean.startsWith(`${rootNoSlash}/`)) return window.location.origin + clean;
    return base + clean;
  };

  /**
   * Shopify cart permalink: /cart/<variant>:<qty>,… rebuilds the same cart on any device
   * (e.g. the customer opens the email on the phone). storefront=true lands on the cart
   * page instead of checkout. Lines of the same variant are merged.
   */
  const restoreUrl = (items) => {
    if (!items.length) return `${base}/cart`;
    const qty = new Map();
    items.forEach((i) => qty.set(i.variant_id, (qty.get(i.variant_id) || 0) + i.quantity));
    const lines = [...qty].map(([id, q]) => `${id}:${q}`).join(',');
    return `${base}/cart/${lines}?storefront=true`;
  };

  const buildCart = (cart) => ({
    items: cart.items.map((i) => ({
      ProductID: i.product_id,
      VariantID: i.variant_id,
      ProductName: i.product_title,
      VariantName: i.variant_title,
      Quantity: i.quantity,
      ItemPrice: money(i.final_price),
      RowTotal: money(i.final_line_price),
      ProductURL: localizedUrl(i.url),
      ImageURL: i.image,
    })),
    ItemCount: cart.item_count,
    total_price: money(cart.total_price),
    Currency: cart.currency,
    CartURL: `${base}/cart`,
    CartRestoreURL: restoreUrl(cart.items),
  });

  /**
   * Extra fields the current event already sends (Brand, Categories, CompareAtPrice, Value).
   * Kept so nothing the flow may rely on disappears. Data comes from the product page
   * (window.LSKlaviyoProduct, printed by the snippet) — only available when the product
   * added is the one on the current product page.
   */
  const productExtras = (added) => {
    const p = window.LSKlaviyoProduct;
    if (!p || String(p.id) !== String(added.ProductID)) return { Value: added.ItemPrice };
    const compareAt = p.compareAt && p.compareAt[added.VariantID];
    return {
      Brand: p.brand,
      Categories: p.categories,
      CompareAtPrice: compareAt ? money(compareAt) : 0,
      Value: added.ItemPrice,
    };
  };

  /**
   * "Added to Cart" event: only for additions, once per addition.
   * @param {object} c Cart built by buildCart().
   * @param {number|string} [addedVariantId] Variant just added.
   */
  const track = (c, addedVariantId) => {
    if (!addedVariantId) return;
    const key = `${addedVariantId}|${c.ItemCount}`;
    if (key === lastKey) return;
    lastKey = key;

    const a = c.items.find((i) => String(i.VariantID) === String(addedVariantId)) || c.items[0];
    if (!a) return;

    klaviyo.track('Added to Cart', {
      Name: a.ProductName,
      ProductID: a.ProductID,
      VariantID: a.VariantID,
      VariantName: a.VariantName,
      URL: a.ProductURL,
      ImageURL: a.ImageURL,
      Price: a.ItemPrice,
      Quantity: a.Quantity,
      ...productExtras(a),
      CartURL: c.CartURL,
      CartRestoreURL: c.CartRestoreURL,
      items: c.items,
      ItemCount: c.ItemCount,
      total_price: c.total_price,
      Currency: c.Currency,
      $value: c.total_price,
    });
  };

  // Quantities per variant of the last cart seen; used to find what an add-to-cart added.
  let previous = null;

  // Exact hint from the Focal theme: its product form dispatches "variant:added" with the line.
  let hint = null;
  document.addEventListener('variant:added', (e) => {
    const v = e.detail && e.detail.variant;
    if (v && v.variant_id) hint = { id: v.variant_id, at: Date.now() };
  });
  const quantities = (cart) =>
    cart.items.reduce((m, i) => m.set(i.variant_id, (m.get(i.variant_id) || 0) + i.quantity), new Map());

  const readCart = () => fetch(`${root}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());

  /**
   * Re-reads the cart and syncs it to Klaviyo.
   * @param {boolean} [isAdd] The cart change came from /cart/add: also track "Added to Cart".
   */
  const doSync = (isAdd) =>
    readCart()
      .then((cart) => {
        const now = quantities(cart);
        let addedVariantId = null;
        if (isAdd) {
          const grown = [...now].find(([id, q]) => q > ((previous && previous.get(id)) || 0));
          if (hint && Date.now() - hint.at < 15000 && now.has(hint.id)) addedVariantId = hint.id;
          else if (grown) addedVariantId = grown[0];
          else if (cart.items[0]) addedVariantId = cart.items[0].variant_id;
          hint = null;
        }
        previous = now;
        if (!window.klaviyo) return;
        const c = buildCart(cart);

        // Profile always holds the current cart (empty list when the cart is emptied).
        // identify is async: wait for it, otherwise the event carries Klaviyo's cached
        // (stale) profile properties and could overwrite CarrelloAttuale with the old cart.
        return Promise.resolve(klaviyo.identify({ CarrelloAttuale: c }))
          .catch(() => {})
          .then(() => track(c, addedVariantId));
      })
      .catch(() => {});

  // Syncs run one after another, so quick consecutive changes compare against the right cart.
  let queue = Promise.resolve();
  const sync = (isAdd) => (queue = queue.then(() => doSync(isAdd)));

  // Initial snapshot, so the first addition can be compared against something.
  queue = readCart()
    .then((cart) => { previous = quantities(cart); })
    .catch(() => {});

  // Notified when requests have finished, whoever made them and however they were made.
  // The browser may deliver several entries at once: one sync per batch is enough, since
  // cart.js is read fresh anyway.
  const onEntries = (list) => {
    let changed = false;
    let added = false;
    list.getEntries().forEach((e) => {
      if (e.initiatorType !== 'fetch' && e.initiatorType !== 'xmlhttprequest') return;
      if (e.responseStatus >= 400) return; // failed request (e.g. 422: out of stock)
      let path = '';
      try { path = new URL(e.name).pathname; } catch (err) { return; }
      const m = CART_ENDPOINT.exec(path);
      if (!m) return;
      changed = true;
      if (m[1] === 'add') added = true;
    });
    if (changed) sync(added);
  };

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver(onEntries).observe({ type: 'resource' });
    } catch (err) { /* very old browser: nothing is sent, same as without this script */ }
  }

  // Manual hook, e.g. for a third-party widget: lsKlaviyoCartSync(true) after an addition.
  window.lsKlaviyoCartSync = sync;
})();
